import { Combobox } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import type { ChartBinding, ChartBindingValue } from '@/entities/dashboards';

type Props = {
  binding: ChartBinding;
  /** Выбранное значение разреза; `null` — разрез не выбран */
  value: ChartBindingValue;
  onChange: (value: ChartBindingValue) => void;
};

/**
 * Переключатель разреза диаграммы в её шапке: величина, которую пользователь выбирает на самом
 * дашборде, не входя в режим редактирования.
 *
 * Значение уходит параметром наполнения, поэтому смена разреза перенаполняет все диаграммы,
 * разрезанные разрезом того же имени, и не трогает остальные. Сам запрос при этом не меняется.
 */
export const ChartSliceSwitcher: FC<Props> = ({ binding, value, onChange }) => {
  const options = useMemo(
    () =>
      (binding.options ?? []).map((option) => ({
        label: String(option),
        value: String(option),
      })),
    [binding.options],
  );

  if (options.length === 0) return null;

  const handleChange = (selected: unknown) => {
    const raw = selected === undefined || selected === null ? null : String(selected);
    if (raw === null) {
      onChange(null);
      return;
    }
    // Значение возвращается тем же видом, каким его дал разрез: число, ушедшее строкой, бэкенд
    // сравнивал бы с числовой колонкой как текст.
    const original = (binding.options ?? []).find((option) => String(option) === raw);
    onChange(original ?? raw);
  };

  return (
    <Combobox
      value={value === null || value === undefined ? undefined : String(value)}
      onChange={handleChange}
      options={options}
      // Имя разреза стоит подсказкой поля: подпись сверху развела бы шапку по высоте с
      // соседними диаграммами, у которых разреза нет.
      placeholder={binding.name}
      isSearchable={options.length > 10}
      size="sm"
      className="w-40 shrink-0"
    />
  );
};
